import type { ReactPortal } from 'react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

// each ID can only be used once for useContextPortal()
// multiple createContextPortal can share a same id though.
export enum ContextPortalID {
  ADD_LISTING_TO_TOUR_CONTEXT = 'AddListingToTourContext',
}

type PortalEventProps = {
  contextPortal: () => ReactPortal;
  subPortalId: string;
};

const generateSubPortalID = () => Math.random().toString(16).slice(2, 11);

const eventDestroyName = (id: ContextPortalID) => `${id}Destroy`;

const eventReadyName = (id: ContextPortalID) => `${id}Ready`;

const eventUpdateName = (id: ContextPortalID, subPortalId: string) =>
  `${id}${subPortalId}Update`;

// when destroyed, remove the portal for that component
const getDestroyEvent = (
  id: ContextPortalID,
  subPortalId: string,
  contextPortal: () => ReactPortal,
) => {
  const eventId = eventDestroyName(id);
  const eventProp = { detail: { contextPortal, subPortalId } };
  return new CustomEvent<PortalEventProps>(eventId, eventProp);
};

// create the portal when the container is loaded.
const getReadyEvent = (
  id: ContextPortalID,
  subPortalId: string,
  contextPortal: () => ReactPortal,
) => {
  const eventId = eventReadyName(id);
  const eventProp = { detail: { contextPortal, subPortalId } };
  return new CustomEvent<PortalEventProps>(eventId, eventProp);
};

const getUpdateEvent = (
  id: ContextPortalID,
  subPortalId: string,
  props: any,
) => {
  const eventId = eventUpdateName(id, subPortalId);
  const eventProp = { detail: { props } };
  return new CustomEvent(eventId, eventProp);
};

/**
 * Use this with createContextPortal,
 * See steps: {@link createContextPortal}
 * @param portalId the unique id of the contextPortal
 */
export const useContextPortal: (
  portalId: ContextPortalID,
) => React.FC = portalId => {
  const [contextPortals, setContextPortals] =
    useState<Record<string, () => ReactPortal>>();

  useEffect(() => {
    const onReady = ({ detail }: any) => {
      setContextPortals(existingPortals => ({
          ...existingPortals,
          [detail.subPortalId]: detail.contextPortal,
        }));
    };
    const onDestroy = ({ detail }: any) => {
      setContextPortals(existingPortals => {
        const result = {
          ...existingPortals,
        };
        delete result[detail.subPortalId];
        return result;
      });
    };
    document.addEventListener(eventReadyName(portalId), onReady);
    document.addEventListener(eventDestroyName(portalId), onDestroy);
    return () => {
      document.removeEventListener(eventReadyName(portalId), onReady);
      document.removeEventListener(eventDestroyName(portalId), onDestroy);
    };
  }, []);

  // reduce unnecessary re-create of the elements
  const ContextPortal = useCallback(
    () =>
      React.createElement(
        React.Fragment,
        null,
        contextPortals && Object.values(contextPortals).map(portal => portal()),
      ),
    [contextPortals],
  );

  return ContextPortal;
};

/**
 * Enable access to react context without having to change component tree.
 * Currently used to solve the problem of accessing react context from angular page.
 * Use this with useContextPortal, Steps:
 * 1. Come up with a unique id, and put it in ContextPortalID enum, make sure createContextPortal and useContextPortal has the same id.
 * 2. Wrap the component that don't have access to context with createContextPortal function,
 * 3. Put useContextPortal in any functional component that has access to the context you want to use
 * 4. Put the return component of useContextPortal in the ReactDOM tree.
 * @param portalId the unique id of the contextPortal
 * @param component the component you want to allow access to some context
 */
export const createContextPortal: <P>(
  portalId: ContextPortalID,
  component: React.FC<P>,
) => React.FC<P> = (portalId, component) => props => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const subPortalIdRef = useRef<string>(generateSubPortalID());

  useEffect(() => {
    const subPortalId = subPortalIdRef.current;
    // by using createPortal, the component is not in the original ReactDOM tree
    // thus need to manually re-render the component when prop changes.
    const DynamicComponent: React.FC<typeof props> = initialProps => {
      const [updatedProps, setUpdatedProps] = useState(initialProps);
      useEffect(() => {
        const updateComponent = ({ detail }: any) => {
          setUpdatedProps({ ...detail.props });
        };
        document.addEventListener(
          eventUpdateName(portalId, subPortalId),
          updateComponent,
        );
        return () => {
          document.removeEventListener(
            eventUpdateName(portalId, subPortalId),
            updateComponent,
          );
        };
      }, []);
      return React.createElement(component, updatedProps);
    };

    const portal = () =>
      ReactDOM.createPortal(
        React.createElement(DynamicComponent, props),
        containerRef.current,
      );

    document.dispatchEvent(getReadyEvent(portalId, subPortalId, portal));

    return () => {
      document.dispatchEvent(getDestroyEvent(portalId, subPortalId, portal));
    };
  }, []);

  // every re-render of this should trigger the re-render of the original component.
  useEffect(() => {
    document.dispatchEvent(
      getUpdateEvent(portalId, subPortalIdRef.current, props),
    );
  });

  return React.createElement('span', { ref: containerRef });
};
